import { CompositeDisposable, isDisposable } from '@gitlab-org/disposable';
import { Logger } from '@gitlab-org/logging';
import { WebviewAddress, WebviewPlugin } from '@gitlab-org/webview-plugin';
import { ThemeProvider, setupWebviewThemeListener } from '@gitlab-org/webview-theme';
import { ExtensionMessageBusProvider } from '../../types';
import { WebviewRuntimeMessageBus } from '../../events';
import { WebviewController } from './webview_controller';
import { WebviewInstanceMessageBus } from './webview_instance_message_bus';

type setupWebviewPluginsProps = {
  plugins: WebviewPlugin[];
  extensionMessageBusProvider: ExtensionMessageBusProvider;
  logger: Logger;
  runtimeMessageBus: WebviewRuntimeMessageBus;
  themeProvider: ThemeProvider;
};

export function setupWebviewPlugins({
  plugins,
  extensionMessageBusProvider,
  logger,
  runtimeMessageBus,
  themeProvider,
}: setupWebviewPluginsProps) {
  logger.debug(`Setting up (${plugins.length}) plugins`);

  const compositeDisposable = new CompositeDisposable();

  for (const plugin of plugins) {
    logger.debug(`Setting up plugin: ${plugin.id}`);
    const extensionMessageBus = extensionMessageBusProvider.getMessageBus(plugin.id);

    const webviewInstanceMessageBusFactory = (address: WebviewAddress) => {
      return new WebviewInstanceMessageBus(address, runtimeMessageBus, logger);
    };

    const webviewController = new WebviewController(
      plugin.id,
      runtimeMessageBus,
      webviewInstanceMessageBusFactory,
      logger,
    );

    compositeDisposable.add(setupWebviewThemeListener(themeProvider, webviewController));

    const disposable = plugin.setup({
      webview: webviewController,
      extension: extensionMessageBus,
    });

    if (isDisposable(disposable)) {
      compositeDisposable.add(disposable);
    }
  }

  logger.debug('All plugins were set up successfully');
  return compositeDisposable;
}
